import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Instagram, MapPin, MessageCircle, CheckCircle2 } from "lucide-react";
import api, { PRACTITIONER, waLink } from "../lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("Message sent! I'll be in touch soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="container-soft pt-16 md:pt-24 pb-12">
        <div className="text-xs uppercase tracking-[0.25em] mb-4" style={{ color: "var(--primary-deep)" }}>Contact</div>
        <h1 className="font-display text-5xl sm:text-6xl mb-6 max-w-3xl" style={{ color: "var(--text-primary)" }}>
          Let's talk, gently.
        </h1>
        <p className="max-w-2xl text-lg leading-relaxed" style={{ color: "var(--text-secondary)" }}>
          Have a question, or just want to say hello before booking? Use whichever channel feels easiest.
        </p>
      </section>

      <section className="container-soft py-12 grid lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-5 space-y-4">
          <ContactItem icon={Mail} label="Email" value={PRACTITIONER.email} href={`mailto:${PRACTITIONER.email}`} testId="contact-email"/>
          <ContactItem icon={Phone} label="Phone" value={PRACTITIONER.phone} testId="contact-phone"/>
          <ContactItem icon={Instagram} label="Instagram" value={`@${PRACTITIONER.instagram}`} href={PRACTITIONER.instagramUrl} testId="contact-instagram"/>
          <ContactItem icon={MapPin} label="Location" value={PRACTITIONER.city} testId="contact-location"/>
          <a href={waLink()} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-full px-6 py-4 transition-all" style={{ background: "#25D366", color: "#fff" }} data-testid="contact-whatsapp-btn">
            <MessageCircle size={18}/> <span>Message me on WhatsApp</span>
          </a>
        </aside>

        <div className="lg:col-span-7">
          {done ? (
            <div className="rounded-3xl p-10 text-center" style={{ background: "var(--surface)", border: "1px solid var(--border-soft)" }} data-testid="contact-thankyou">
              <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: "var(--accent-lavender)" }}>
                <CheckCircle2 size={24} style={{ color: "var(--primary-deep)" }}/>
              </div>
              <h2 className="font-display text-3xl mb-3" style={{ color: "var(--text-primary)" }}>Message received</h2>
              <p style={{ color: "var(--text-secondary)" }}>I'll get back to you as soon as I can. Thank you for reaching out.</p>
              <button onClick={() => setDone(false)} className="mt-6 rounded-full px-6 py-3 text-sm" style={{ background: "var(--primary)", color: "#fff" }}>Send another message</button>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl p-8 md:p-10 space-y-5" style={{ background: "var(--surface)", border: "1px solid var(--border-soft)" }}>
              <h2 className="font-display text-3xl mb-2" style={{ color: "var(--text-primary)" }}>Send a message</h2>
              <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>I'll get back to you within 24-48 hours.</p>
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Your Name *"><input required value={form.name} onChange={set("name")} className="cf-input" data-testid="contact-name"/></Field>
                <Field label="Email *"><input type="email" required value={form.email} onChange={set("email")} className="cf-input" data-testid="contact-email-input"/></Field>
              </div>
              <Field label="Phone (optional)"><input value={form.phone} onChange={set("phone")} className="cf-input" data-testid="contact-phone-input"/></Field>
              <Field label="Your Message *">
                <textarea rows={6} required value={form.message} onChange={set("message")} className="cf-input resize-none" data-testid="contact-message"/>
              </Field>
              <button type="submit" disabled={loading} className="rounded-full px-7 py-3.5 text-sm disabled:opacity-60" style={{ background: "var(--primary)", color: "#fff" }} data-testid="contact-submit">
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          )}
        </div>
      </section>

      <style>{`
        .cf-input { width:100%; padding:12px 14px; border-radius:14px; border:1px solid var(--border-soft); background:var(--bg-primary); color:var(--text-primary); font-size:15px; transition:border-color .2s, box-shadow .2s; }
        .cf-input:focus { border-color:var(--primary); box-shadow: 0 0 0 3px rgba(143,163,150,0.18); outline:none; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm mb-2" style={{ color: "var(--text-secondary)" }}>{label}</span>
      {children}
    </label>
  );
}

function ContactItem({ icon: Icon, label, value, href, testId }) {
  const Inner = (
    <div className="flex items-center gap-4 p-5 rounded-2xl transition-colors hover:bg-[color:var(--bg-secondary)]" style={{ background: "var(--surface)", border: "1px solid var(--border-soft)" }} data-testid={testId}>
      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--accent-lavender)" }}>
        <Icon size={16} style={{ color: "var(--primary-deep)" }}/>
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--primary-deep)" }}>{label}</div>
        <div className="text-sm" style={{ color: "var(--text-primary)" }}>{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">{Inner}</a> : Inner;
}
